import React, { useCallback, useEffect, useState } from "react";
import * as api from "./csaApi";

/**
 * Putting an auditor on an audit, or taking them off (design §7d).
 *
 * The endpoint existed before this did, which meant a PM could see a conflict
 * on the schedule and had no way to act on it — the same gap the review
 * endpoints had before they got a screen.
 *
 * The interaction is deliberately two-step when it needs to be. Choosing
 * somebody already booked shows the clash *before* anything is saved; going
 * ahead is a second, separate click that says so in words. §7d is firm that the
 * consequence must be visible rather than silent: the other audit really loses
 * its auditor, and this panel says that will happen before it happens, then
 * reports what it did.
 */
export default function CsaAssign({ audit, onDone, onCancel }) {
  const [people, setPeople] = useState([]);
  const [chosen, setChosen] = useState(
    audit.prepared_by == null ? "" : String(audit.prepared_by)
  );
  const [conflicts, setConflicts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api
      .listPeople("Auditor")
      .then((r) => setPeople((r && r.people) || []))
      .catch(() => setPeople([]));
  }, []);

  /* Check as soon as somebody is picked — before saving, not after. */
  const check = useCallback(() => {
    if (!chosen || Number(chosen) === Number(audit.prepared_by)) {
      setConflicts(null);
      return;
    }
    api
      .scheduleConflicts(chosen, audit.start_date, audit.end_date, audit.audit_id)
      .then((r) => setConflicts(r && r.conflicts && r.conflicts.length ? r : null))
      .catch(() => setConflicts(null));
  }, [chosen, audit]);

  useEffect(check, [check]);

  /*
   * `target` is passed rather than read from state.
   *
   * Unassign used to call setChosen("") and then save(), but React state is not
   * applied synchronously — save() still saw the old value from its closure and
   * cheerfully re-assigned the person it was meant to remove. Naming the target
   * removes the possibility rather than relying on ordering.
   */
  const save = async (take, target = chosen) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.assignAudit(
        audit.audit_id,
        target === "" || target == null ? null : Number(target),
        take
      );
      setResult(r);
      // Left open for a moment when something else was displaced, so the PM
      // sees which audits they just emptied rather than watching it vanish.
      if (!r.freed || !r.freed.length) onDone(r);
    } catch (e) {
      if (e.isConflict && e.body && e.body.conflicts) {
        setConflicts({ conflicts: e.body.conflicts, summary: e.message });
        setError(e.body.consequence || e.message);
      } else {
        setError(e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const changed = String(chosen) !== String(audit.prepared_by == null ? "" : audit.prepared_by);

  /* ---------------- what just happened ---------------- */
  if (result) {
    return (
      <div className="asg">
        <h4>Done</h4>
        <p className="asg-note">{result.note}</p>
        <ul className="asg-freed">
          {(result.freed || []).map((f) => (
            <li key={f.audit_id}>
              <strong>{f.site}</strong> ({f.report_id}) now has no auditor.
            </li>
          ))}
        </ul>
        <div className="asg-actions">
          <button type="button" className="ca-save" onClick={() => onDone(result)}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="asg">
      <h4>Who does this audit?</h4>

      <p className="asg-what">
        <strong>{audit.organization}</strong> · {audit.site}
        <span className="asg-when">
          {audit.start_date} to {audit.end_date}
        </span>
      </p>

      {error ? <p className="ca-error">{error}</p> : null}

      <label htmlFor="asg-who">Auditor</label>
      <select id="asg-who" value={chosen} onChange={(e) => setChosen(e.target.value)}>
        <option value="">Nobody — leave it unassigned</option>
        {people.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>

      {conflicts ? (
        <div className="asg-clash">
          <strong>{conflicts.summary}</strong>
          <ul>
            {conflicts.conflicts.map((c) => (
              <li key={c.audit_id}>
                {c.organization} · {c.site} — {c.start_date} to {c.end_date}
                {/* Who put them on it, which is who to speak to — not merely
                    who set the audit up. Those differ after a reassignment. */}
                {c.booked_by ? (
                  <span className="sch-owner">booked by {c.booked_by}</span>
                ) : c.owned_by ? (
                  <span className="sch-owner">audit owned by {c.owned_by}</span>
                ) : null}
              </li>
            ))}
          </ul>
          <p>
            Going ahead takes them off {conflicts.conflicts.length === 1 ? "that audit" : "those audits"},
            which will then have no auditor until their own project manager acts.
          </p>
        </div>
      ) : null}

      <div className="asg-actions">
        <button type="button" className="fw-plain" disabled={busy} onClick={onCancel}>
          Cancel
        </button>

        {/*
          Taking somebody off is its own act, not a dropdown option somebody has
          to find. Only offered when there is somebody to take off.
        */}
        {audit.prepared_by != null ? (
          <button
            type="button"
            className="fw-plain"
            disabled={busy}
            title="Leave this audit with no auditor"
            onClick={() => { setChosen(""); save(false, ""); }}
          >
            {busy ? "Working…" : "Unassign"}
          </button>
        ) : null}
        {conflicts ? (
          <button
            type="button"
            className="fw-danger"
            disabled={busy}
            onClick={() => save(true)}
          >
            {busy ? "Assigning…" : "Take them anyway"}
          </button>
        ) : (
          <button
            type="button"
            className="ca-save"
            disabled={busy || !changed}
            onClick={() => save(false)}
          >
            {busy ? "Saving…" : chosen === "" ? "Leave unassigned" : "Assign"}
          </button>
        )}
      </div>
    </div>
  );
}
